import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, FileSpreadsheet, ChevronDown, ChevronUp, ArrowUpDown, RefreshCw, Search, Ship, X } from "lucide-react";

const cop = (n) => `$ ${Math.round(n || 0).toLocaleString("es-CO")}`;
const num = (n) => Number(n || 0).toLocaleString("es-CO", { maximumFractionDigits: 2 });
const TEXT_KEYS = new Set(["nombre", "familia", "key"]);
const GRUPOS = [["producto", "Producto"], ["familia", "Familia"], ["categoria", "Categoría"], ["contenedor", "Contenedor"]];

/** Productos → Inventario: valor de la mercancía por lote, producto, familia, categoría y contenedor. */
export default function InventarioValorizadoPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [grupo, setGrupo] = useState("producto");
  const [sp, setSp] = useSearchParams();
  const contenedor = sp.get("contenedor") || "";
  const setContenedor = (c) => setSp((prev) => { const n = new URLSearchParams(prev); if (c) n.set("contenedor", c); else n.delete("contenedor"); return n; }, { replace: true });
  const [categoria, setCategoria] = useState("");
  const [soloStock, setSoloStock] = useState(true);
  const [sort, setSort] = useState({ key: "valor_costo", dir: -1 });
  const [abierto, setAbierto] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/inventario/valorizado"); setData(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar el inventario"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filas = useMemo(() => {
    if (!data) return [];
    const t = q.trim().toLowerCase();
    return data.filas.filter((f) =>
      (!soloStock || f.stock > 0) &&
      (!categoria || f.categoria === categoria) &&
      (!contenedor || f.lotes.some((l) => l.contenedor === contenedor)) &&
      (!t || [f.nombre, f.codigo, f.familia, f.categoria, f.color].some((s) => (s || "").toLowerCase().includes(t))));
  }, [data, q, soloStock, categoria, contenedor]);

  const grupos = useMemo(() => {
    if (grupo === "producto") return null;
    const m = {};
    filas.forEach((f) => {
      const keys = grupo === "contenedor" ? [...new Set(f.lotes.map((l) => l.contenedor))] : [f[grupo]];
      keys.forEach((k) => {
        const g = m[k] ||= { key: k, productos: 0, stock: 0, valor_costo: 0, valor_minimo: 0, valor_sugerido: 0, items: [] };
        if (grupo === "contenedor") {
          const ls = f.lotes.filter((l) => l.contenedor === k);
          g.stock += ls.reduce((a, l) => a + l.disponible, 0); g.valor_costo += ls.reduce((a, l) => a + l.valor_costo, 0);
          g.valor_minimo += ls.reduce((a, l) => a + l.valor_minimo, 0); g.valor_sugerido += ls.reduce((a, l) => a + l.valor_sugerido, 0);
        } else { g.stock += f.stock; g.valor_costo += f.valor_costo; g.valor_minimo += f.valor_minimo; g.valor_sugerido += f.valor_sugerido; }
        g.productos += 1; g.items.push(f);
      });
    });
    return Object.values(m).map((g) => ({ ...g, utilidad_potencial: g.valor_sugerido - g.valor_costo, margen_pct: g.valor_sugerido ? ((g.valor_sugerido - g.valor_costo) / g.valor_sugerido) * 100 : null }));
  }, [filas, grupo]);

  const ordenar = (arr) => [...arr].sort((a, b) => {
    const va = a[sort.key], vb = b[sort.key];
    if (TEXT_KEYS.has(sort.key)) return String(va || "").localeCompare(String(vb || ""), "es") * sort.dir;
    const na = va == null || Number.isNaN(va), nb = vb == null || Number.isNaN(vb);
    if (na || nb) return na && nb ? 0 : na ? 1 : -1;
    return (va - vb) * sort.dir;
  });
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key ? -s.dir : (TEXT_KEYS.has(key) ? 1 : -1) }));

  const totales = useMemo(() => filas.reduce((t, f) => ({ stock: t.stock + f.stock, lotes_und: t.lotes_und + f.stock_lotes, valor_costo: t.valor_costo + f.valor_costo, valor_minimo: t.valor_minimo + f.valor_minimo, valor_sugerido: t.valor_sugerido + f.valor_sugerido, lotes: t.lotes + f.lotes.length }), { stock: 0, lotes_und: 0, valor_costo: 0, valor_minimo: 0, valor_sugerido: 0, lotes: 0 }), [filas]);

  const exportar = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (contenedor) params.set("contenedor", contenedor);
      if (categoria) params.set("categoria", categoria);
      if (soloStock) params.set("solo_con_stock", "true");
      const res = await api.get(`/inventario/valorizado/excel?${params}`, { responseType: "blob" });
      const href = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = href; a.download = `inventario_valorizado_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click(); URL.revokeObjectURL(href);
    } catch { toast.error("No se pudo exportar"); }
    finally { setExporting(false); }
  };

  if (loading && !data) return <div className="flex items-center gap-2 text-muted-foreground p-8" data-testid="inventario-loading"><Loader2 className="w-4 h-4 animate-spin" /> Calculando inventario…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="inventario-valorizado-page">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Inventario</div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-1">¿Cuánto vale mi mercancía?</h1>
          <p className="text-sm text-muted-foreground mt-1">Valor por lote a costo real (FIFO), y lo que vale si la vendes a precio mínimo o sugerido. Incluye el inventario INICIAL y cada contenedor.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="h-10" data-testid="inventario-refrescar-btn"><RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualizar</Button>
          <Button onClick={exportar} disabled={exporting} className="h-10" data-testid="inventario-exportar-btn"><FileSpreadsheet className="w-4 h-4 mr-2" /> {exporting ? "Generando…" : "Exportar Excel"}</Button>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi label="Valor a costo" value={cop(totales.valor_costo)} sub={`${num(totales.stock)} unidades${totales.stock !== totales.lotes_und ? ` · ${num(totales.lotes_und)} en lotes` : ""}`} accent testid="inv-kpi-costo" />
        <Kpi label="Valor a precio mínimo" value={cop(totales.valor_minimo)} sub={`utilidad ${cop(totales.valor_minimo - totales.valor_costo)}`} testid="inv-kpi-minimo" />
        <Kpi label="Valor a precio sugerido" value={cop(totales.valor_sugerido)} sub={`utilidad ${cop(totales.valor_sugerido - totales.valor_costo)}`} testid="inv-kpi-sugerido" />
        <Kpi label="Margen potencial" value={totales.valor_sugerido ? `${(((totales.valor_sugerido - totales.valor_costo) / totales.valor_sugerido) * 100).toFixed(1)}%` : "—"} sub="sobre precio sugerido" testid="inv-kpi-margen" />
        <Kpi label="Productos" value={num(filas.length)} sub={`${new Set(filas.map((f) => f.familia)).size} familias · ${new Set(filas.map((f) => f.categoria)).size} categorías`} testid="inv-kpi-productos" />
        <Kpi label="Lotes activos" value={num(totales.lotes)} sub={`${data.contenedores.length} contenedor(es)`} testid="inv-kpi-lotes" />
      </div>

      <Contenedores data={data} sel={contenedor} onSel={(c) => setContenedor(c === contenedor ? "" : c)} />
      {contenedor && (() => {
        const info = data.contenedores.find((c) => c.contenedor === contenedor);
        return (
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 px-4 py-2.5 text-sm" data-testid="inventario-filtro-contenedor">
            <Ship className="w-4 h-4 text-primary" />
            <span>Productos que entraron en <b>{contenedor}</b>: <b>{filas.length}</b> producto(s){info ? <> · {num(info.unidades)} und disponibles · {cop(info.valor_costo)} a costo</> : null}</span>
            <button type="button" onClick={() => setContenedor("")} className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="inventario-quitar-filtro-contenedor"><X className="w-3.5 h-3.5" /> Quitar filtro</button>
          </div>
        );
      })()}

      <div className="flex flex-wrap items-center gap-2" data-testid="inventario-filtros">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto, código, familia…" className="h-10 pl-9" data-testid="inventario-buscar" />
        </div>
        <select value={categoria} onChange={(e) => setCategoria(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="inventario-categoria">
          <option value="">Todas las categorías</option>
          {data.categorias.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={contenedor} onChange={(e) => setContenedor(e.target.value)} className="h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="inventario-contenedor">
          <option value="">Todos los contenedores</option>
          {data.contenedores.map((c) => <option key={c.contenedor} value={c.contenedor}>{c.contenedor}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm h-10 px-3 rounded-md border border-input cursor-pointer" data-testid="inventario-solo-stock">
          <input type="checkbox" checked={soloStock} onChange={(e) => setSoloStock(e.target.checked)} className="accent-primary" /> Solo con stock
        </label>
        <div className="flex rounded-md border border-input overflow-hidden" data-testid="inventario-agrupar">
          {GRUPOS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setGrupo(k); setAbierto(null); }} className={`h-10 px-3 text-sm ${grupo === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`inventario-agrupar-${k}`}>{l}</button>
          ))}
        </div>
      </div>

      {grupo === "producto"
        ? <TablaProductos filas={ordenar(filas)} sort={sort} onSort={toggleSort} abierto={abierto} setAbierto={setAbierto} contenedor={contenedor} onContenedor={setContenedor} />
        : <TablaGrupos grupos={ordenar(grupos)} grupo={grupo} sort={sort} onSort={toggleSort} abierto={abierto} setAbierto={setAbierto} contenedor={contenedor} onContenedor={setContenedor} />}
    </div>
  );
}

function Contenedores({ data, sel, onSel }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-1" data-testid="inventario-contenedores">
      {data.contenedores.map((c) => (
        <button key={c.contenedor} type="button" onClick={() => onSel(c.contenedor)} className={`min-w-[200px] text-left rounded-xl border p-3 transition-colors ${sel === c.contenedor ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/40"}`} data-testid={`inventario-cont-${c.contenedor}`}>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">{c.contenedor}</div>
          <div className="text-lg font-bold tabular-nums">{cop(c.valor_costo)}</div>
          <div className="text-[11px] text-muted-foreground">{num(c.unidades)} und · {c.lotes} lote(s) · {c.productos} prod.</div>
          <div className="text-[11px] text-muted-foreground">a sugerido {cop(c.valor_sugerido)}</div>
        </button>
      ))}
    </div>
  );
}

function Th({ label, k, sort, onSort, right = true }) {
  const active = sort.key === k;
  return (
    <th className={`p-2 ${right ? "text-right" : "text-left"} select-none cursor-pointer whitespace-nowrap`} onClick={() => onSort(k)} data-testid={`inv-th-${k}`}>
      <span className={`inline-flex items-center gap-1 ${active ? "text-primary" : ""}`}>{label}{active ? (sort.dir < 0 ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}</span>
    </th>
  );
}

function TablaProductos({ filas, sort, onSort, abierto, setAbierto, contenedor, onContenedor }) {
  return (
    <div className="rounded-xl border border-border overflow-auto max-h-[70vh]" data-testid="inventario-tabla-productos">
      <table className="w-full text-xs">
        <thead className="bg-muted/60 sticky top-0 z-10"><tr>
          <Th label="Producto" k="nombre" sort={sort} onSort={onSort} right={false} /><Th label="Familia" k="familia" sort={sort} onSort={onSort} right={false} />
          <Th label="Stock" k="stock" sort={sort} onSort={onSort} />
          {contenedor && <th className="p-2 text-right whitespace-nowrap text-primary" title={`Unidades que entraron en ${contenedor} y cuántas quedan de ese lote`}>Entró / queda</th>}
          <Th label="Costo prom." k="costo_promedio" sort={sort} onSort={onSort} />
          <Th label="P. mín." k="precio_minimo" sort={sort} onSort={onSort} /><Th label="P. sug." k="precio_sugerido" sort={sort} onSort={onSort} />
          <Th label="Valor costo" k="valor_costo" sort={sort} onSort={onSort} /><Th label="Valor mín." k="valor_minimo" sort={sort} onSort={onSort} />
          <Th label="Valor sug." k="valor_sugerido" sort={sort} onSort={onSort} /><Th label="Margen %" k="margen_pct" sort={sort} onSort={onSort} />
        </tr></thead>
        <tbody>
          {filas.map((f, i) => (
            <FilaProducto key={f.producto_id} f={f} i={i} open={abierto === f.producto_id} onToggle={() => setAbierto(abierto === f.producto_id ? null : f.producto_id)} contenedor={contenedor} onContenedor={onContenedor} />
          ))}
          {filas.length === 0 && <tr><td colSpan={11} className="p-6 text-center text-muted-foreground">Sin productos para estos filtros.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function FilaProducto({ f, i, open, onToggle, nested, contenedor, onContenedor }) {
  const delCont = contenedor ? f.lotes.filter((l) => l.contenedor === contenedor) : [];
  const entro = delCont.reduce((a, l) => a + l.recibida, 0), queda = delCont.reduce((a, l) => a + l.disponible, 0);
  return (
    <>
      <tr className={`border-t border-border/60 tabular-nums cursor-pointer hover:bg-muted/30 ${nested ? "bg-muted/10" : ""}`} onClick={onToggle} data-testid={`inv-fila-${i}`}>
        <td className="p-2 max-w-[260px]"><div className="truncate font-medium">{f.nombre}</div><div className="text-muted-foreground">{f.codigo}{f.stock_negativo ? <span className="ml-1 text-rose-600" title="Stock negativo en la ficha: se valora en 0">· stock negativo</span> : null}{f.descuadre ? <span className="ml-1 text-amber-600" title="Stock de la ficha distinto a la suma de lotes">· descuadre {f.descuadre > 0 ? "+" : ""}{f.descuadre}</span> : null}</div></td>
        <td className="p-2 text-muted-foreground truncate max-w-[180px]">{f.familia}</td>
        <td className="p-2 text-right">{num(f.stock)}</td>
        {contenedor && <td className="p-2 text-right tabular-nums whitespace-nowrap" data-testid={`inv-cont-entro-${i}`}><span className="font-semibold">{num(entro)}</span> <span className="text-muted-foreground">/ {num(queda)}</span></td>}
        <td className="p-2 text-right">{cop(f.costo_promedio)}{f.fuente_costo === "ficha" && <span className="text-muted-foreground" title="Sin lotes: costo de ficha">*</span>}</td>
        <td className="p-2 text-right">{cop(f.precio_minimo)}</td>
        <td className="p-2 text-right">{cop(f.precio_sugerido)}</td>
        <td className="p-2 text-right font-semibold">{cop(f.valor_costo)}</td>
        <td className="p-2 text-right">{cop(f.valor_minimo)}</td>
        <td className="p-2 text-right">{cop(f.valor_sugerido)}</td>
        <td className={`p-2 text-right ${f.margen_pct != null && f.margen_pct < 10 ? "text-rose-600 font-semibold" : ""}`}>{f.margen_pct != null ? `${f.margen_pct}%` : "—"}</td>
      </tr>
      {open && (
        <tr className="bg-muted/20" data-testid={`inv-lotes-${i}`}>
          <td colSpan={contenedor ? 11 : 10} className="p-2">
            {f.lotes.length === 0 ? <div className="text-muted-foreground">Sin lotes activos: valorado a costo de ficha.</div> : (
              <table className="w-full text-[11px] tabular-nums">
                <thead><tr className="text-muted-foreground"><th className="text-left p-1">Contenedor</th><th className="text-left p-1">Recibido el</th><th className="text-right p-1">Recibido</th><th className="text-right p-1">Disponible</th><th className="text-right p-1">Costo unit.</th><th className="text-right p-1">Valor costo</th><th className="text-right p-1">Valor mín.</th><th className="text-right p-1">Valor sug.</th></tr></thead>
                <tbody>{f.lotes.map((l) => (
                  <tr key={l.lote_id} className={`border-t border-border/40 ${contenedor && l.contenedor === contenedor ? "bg-primary/10" : ""}`} data-testid={`inv-lote-${l.lote_id}`}>
                    <td className="p-1">
                      <button type="button" onClick={(e) => { e.stopPropagation(); onContenedor?.(l.contenedor === contenedor ? "" : l.contenedor); }} className="font-semibold text-primary hover:underline" title={l.contenedor === contenedor ? "Quitar filtro por este contenedor" : "Ver todos los productos que entraron en este contenedor"} data-testid={`inv-lote-cont-${l.lote_id}`}>{l.contenedor}</button>
                      {l.desglose_factor ? <span className="ml-1 text-violet-600">×{l.desglose_factor}</span> : null}
                    </td><td className="p-1">{l.fecha || "—"}</td>
                    <td className="p-1 text-right">{num(l.recibida)}</td><td className="p-1 text-right">{num(l.disponible)}</td><td className="p-1 text-right">{cop(l.costo_unitario)}</td>
                    <td className="p-1 text-right font-semibold">{cop(l.valor_costo)}</td><td className="p-1 text-right">{cop(l.valor_minimo)}</td><td className="p-1 text-right">{cop(l.valor_sugerido)}</td>
                  </tr>))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function TablaGrupos({ grupos, grupo, sort, onSort, abierto, setAbierto, contenedor, onContenedor }) {
  const label = GRUPOS.find(([k]) => k === grupo)?.[1];
  return (
    <div className="rounded-xl border border-border overflow-auto max-h-[70vh]" data-testid={`inventario-tabla-${grupo}`}>
      <table className="w-full text-xs">
        <thead className="bg-muted/60 sticky top-0 z-10"><tr>
          <Th label={label} k="key" sort={sort} onSort={onSort} right={false} /><Th label="Productos" k="productos" sort={sort} onSort={onSort} />
          <Th label="Stock" k="stock" sort={sort} onSort={onSort} /><Th label="Valor costo" k="valor_costo" sort={sort} onSort={onSort} />
          <Th label="Valor mín." k="valor_minimo" sort={sort} onSort={onSort} /><Th label="Valor sug." k="valor_sugerido" sort={sort} onSort={onSort} />
          <Th label="Utilidad potencial" k="utilidad_potencial" sort={sort} onSort={onSort} /><Th label="Margen %" k="margen_pct" sort={sort} onSort={onSort} />
        </tr></thead>
        <tbody>
          {grupos.map((g, i) => (
            <GrupoFila key={g.key} g={g} i={i} open={abierto === g.key} onToggle={() => setAbierto(abierto === g.key ? null : g.key)} grupo={grupo} contenedor={contenedor} onContenedor={onContenedor} />
          ))}
          {grupos.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">Sin datos para estos filtros.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function GrupoFila({ g, i, open, onToggle, grupo, contenedor, onContenedor }) {
  const [sub, setSub] = useState(null);
  const contFila = grupo === "contenedor" ? g.key : contenedor;
  return (
    <>
      <tr className="border-t border-border/60 tabular-nums cursor-pointer hover:bg-muted/30 font-medium" onClick={onToggle} data-testid={`inv-grupo-${i}`}>
        <td className="p-2 max-w-[300px]"><div className="flex items-center gap-1">{open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}<span className="truncate">{g.key}</span>
          {grupo === "contenedor" && <button type="button" onClick={(e) => { e.stopPropagation(); onContenedor?.(g.key); }} className="ml-2 text-[10px] font-semibold text-primary hover:underline whitespace-nowrap" title="Filtrar toda la vista por este contenedor" data-testid={`inv-grupo-filtrar-${i}`}>filtrar →</button>}
        </div></td>
        <td className="p-2 text-right">{g.productos}</td><td className="p-2 text-right">{num(g.stock)}</td>
        <td className="p-2 text-right font-semibold">{cop(g.valor_costo)}</td><td className="p-2 text-right">{cop(g.valor_minimo)}</td><td className="p-2 text-right">{cop(g.valor_sugerido)}</td>
        <td className="p-2 text-right">{cop(g.utilidad_potencial)}</td>
        <td className={`p-2 text-right ${g.margen_pct != null && g.margen_pct < 10 ? "text-rose-600" : ""}`}>{g.margen_pct != null ? `${g.margen_pct.toFixed(1)}%` : "—"}</td>
      </tr>
      {open && (
        <tr><td colSpan={8} className="p-0 pl-6">
          <table className="w-full text-xs">
            <thead><tr className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted/30">
              <th className="text-left p-2">Producto</th><th className="text-left p-2">Familia</th><th className="text-right p-2">Stock</th>{contFila && <th className="text-right p-2 text-primary">Entró / queda</th>}<th className="text-right p-2">Costo prom.</th><th className="text-right p-2">P. mín.</th><th className="text-right p-2">P. sug.</th><th className="text-right p-2">Valor costo</th><th className="text-right p-2">Valor mín.</th><th className="text-right p-2">Valor sug.</th><th className="text-right p-2">Margen %</th>
            </tr></thead>
            <tbody>{[...g.items].sort((a, b) => b.valor_costo - a.valor_costo).map((f, j) => (
              <FilaProducto key={f.producto_id} f={f} i={`${i}-${j}`} nested open={sub === f.producto_id} onToggle={() => setSub(sub === f.producto_id ? null : f.producto_id)} contenedor={contFila} onContenedor={onContenedor} />
            ))}</tbody>
          </table>
        </td></tr>
      )}
    </>
  );
}

function Kpi({ label, value, sub, accent, testid }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-xl font-bold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground truncate" title={sub}>{sub}</div>}
    </div>
  );
}
